package com.example.mystybag.appCore.logic

import com.arkivanov.decompose.router.stack.pop
import com.arkivanov.decompose.router.stack.popTo
import com.arkivanov.decompose.router.stack.replaceAll
import com.arkivanov.essenty.backhandler.BackDispatcher
import com.example.mystybag.appCore.logic.pages.BagViewModel
import com.example.mystybag.appCore.logic.pages.DeliveryPageViewModel
import com.example.mystybag.appCore.logic.pages.HomePageViewModel
import com.example.mystybag.appCore.logic.pages.ProfilePageViewModel
import com.example.mystybag.appCore.logic.pages.ReservationDetailsBottomSheetViewModel
import com.example.mystybag.appCore.logic.pages.LanguageSelectionBottomSheetViewModel
import com.example.mystybag.common.logic.BSNavigatorLanguageSelection
import com.example.mystybag.common.logic.NavigatorProfilePage
import com.example.mystybag.communication.Communication
import com.example.mystybag.commonData.expected.getOS
import com.example.mystybag.commonData.helpers.PlatformDefines.IOS
import com.example.mystybag.core.defines.UtilsDefines
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_CODE
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_EMAIL
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_EMAIL
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_JWT
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_USE_BIOMETRIC_FOR_MERCHANT
import com.example.mystybag.core.expected.NavigationHelper
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.expected.logException
import com.example.mystybag.core.extensions.equalsIgnoreCase
import com.example.mystybag.core.extensions.safeCast
import com.example.mystybag.core.security.isJwtExpired
import com.example.mystybag.biometricUtils.expected.BiometricsHandler
import com.example.mystybag.biometricUtils.helpers.BiometricAuthenticationType
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.AppViewModel
import com.example.mystybag.core.logic.base.BaseViewModel
import com.example.mystybag.core.logic.base.BottomSheetChild
import com.example.mystybag.core.logic.base.EmptyViewModel
import com.example.mystybag.core.logic.base.FeatureManager
import com.example.mystybag.core.logic.base.MainCallback
import com.example.mystybag.core.logic.base.NavigatorConfig
import com.example.mystybag.core.logic.base.ParentCallback
import com.example.mystybag.core.logic.base.UIBottomSheetConfig
import com.example.mystybag.core.logic.helpers.DialogResultType
import com.example.mystybag.common.logic.InternetTroubleshootingViewModel
import com.example.mystybag.common.logic.NavigatorMerchantLogin
import com.example.mystybag.common.logic.NavigatorStaffLogin
import com.example.mystybag.common.logic.NavigatorInternetTroubleshooting
import com.example.mystybag.commonData.expected.isInRelease
import com.example.mystybag.notificationsUtils.expected.PushProviderHelper.setupPlatformHelper
import com.example.mystybag.remoteLogger.RemoteLogger
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlin.reflect.KClass
import com.example.mystybag.appCore.helpers.PushTokenServerSync
import com.example.mystybag.appCore.helpers.ReLoginHelper
import com.example.mystybag.logoutUtils.helpers.LogoutMethods
import com.example.mystybag.logoutUtils.helpers.LogoutMethods.logout
import com.example.mystybag.contacts.logic.NavigatorPickContact
import com.example.mystybag.url.logic.NavigatorURLView
import com.example.mystybag.url.logic.URLViewPaymentCallback
import com.example.mystybag.url.logic.URLViewViewModel
import com.example.mystybag.contacts.logic.PickContactViewModel
import com.example.mystybag.core.logic.base.FeatureManagers
import com.example.mystybag.core.logic.base.last
import com.example.mystybag.login.logic.FeatureLoginAndRegistrationViewModel
import com.example.mystybag.login.logic.LoginInterface
import com.example.mystybag.login.logic.NavigatorFeatureLoginAndRegistration
import com.example.mystybag.login.logic.NavigatorMerchantRegistrationStep7
import com.example.mystybag.login.logic.NavigatorMerchantRegistrationStep8
import com.example.mystybag.login.logic.NavigatorMerchantRegistrationStep9
import com.example.mystybag.login.logic.NavigatorMerchantRegistrationStep9B
import com.example.mystybag.login.logic.NavigatorAddMerchantLocationFromDashboard
import com.example.mystybag.login.logic.NavigatorAddFirstBagPrompt
import com.example.mystybag.login.logic.NavigatorAddLocationPrompt
import com.example.mystybag.appCore.logic.addMerchantLocation.MerchantRegistrationAddMerchantLocationDetailsViewModel
import com.example.mystybag.appCore.logic.addMerchantLocation.MerchantRegistrationReviewStoreDetailsViewModel
import com.example.mystybag.appCore.logic.addMerchantLocation.MerchantRegistrationAddDescriptionViewModel
import com.example.mystybag.appCore.logic.addMerchantLocation.MerchantRegistrationMenuPhotosViewModel
import com.example.mystybag.pdf.logic.NavigatorPdfFile
import com.example.mystybag.pdf.logic.NavigatorPdfUrl
import com.example.mystybag.pdf.logic.PdfUrlViewModel
import com.example.mystybag.pdf.logic.PdfFileViewModel
import com.example.mystybag.cropImage.logic.NavigatorProfileCropImage
import com.example.mystybag.cropImage.logic.ProfileImageCropperViewModel

//used for iOS Swipe Back Gesture
val iOSBackDispatcher = BackDispatcher()

/**
 * Chooses initial screen so WelcomeView is never shown on re-open.
 * - Client logged in (email+code, no merchant JWT) -> Main.
 * - Merchant with saved email and biometric enabled (and available) -> MerchantLogin screen; biometric is triggered there.
 * - Merchant with valid JWT and no biometric -> Main (directly in app).
 * - Merchant with saved email, no biometric -> MerchantLogin.
 * - Otherwise -> Login feature with Welcome (first-time users).
 */
private fun getInitialNavigatorConfig(): NavigatorConfig {
    val mail = Preferences.getString(key = PREFERENCES_EMAIL, "")
    val code = Preferences.getString(key = PREFERENCES_CODE, "")
    val merchantJwt = Preferences.getString(key = PREFERENCES_MERCHANT_JWT, "")
    val merchantEmail = Preferences.getString(key = PREFERENCES_MERCHANT_EMAIL, "")
    val useBiometricForMerchant = Preferences.getBoolean(key = PREFERENCES_USE_BIOMETRIC_FOR_MERCHANT, false)

    val biometricAvailable =
        BiometricsHandler.getAuthenticationType() != BiometricAuthenticationType.NONE &&
            BiometricsHandler.isBiometricScannerAvailableAndSet()

    // Client already logged in (no merchant session) -> Main
    if (mail.isNotEmpty() && code.isNotEmpty() && merchantJwt.isEmpty()) {
        return NavigatorMain
    }
    // Merchant with biometric enabled -> open MerchantLogin first; biometric is triggered on that screen
    if (merchantEmail.isNotEmpty() && useBiometricForMerchant && biometricAvailable) {
        return NavigatorFeatureLoginAndRegistration(initialChild = NavigatorMerchantLogin)
    }
    // Merchant with valid JWT and no biometric -> open directly in app
    if (merchantJwt.isNotEmpty() && !isJwtExpired(merchantJwt)) {
        Communication.token = merchantJwt
        return NavigatorMain
    }
    // Returning merchant (saved email), no biometric -> Login screen
    if (merchantEmail.isNotEmpty()) {
        return NavigatorFeatureLoginAndRegistration(initialChild = NavigatorMerchantLogin)
    }
    return NavigatorFeatureLoginAndRegistration()
}

class MystyBagDigitalWalletViewModel(
    context             : AppContext,
    private val config  : FeatureManager? = null
): AppViewModel(
    context = context,
    config  = getInitialNavigatorConfig()
),
    LoginInterface,
    MainCallback
{

    companion object {
        // Client pages
        const val PAGE_HOME         = 0
        const val PAGE_DELIVERY     = 1
        const val PAGE_FAVORITES    = 2
        const val PAGE_PROFILE      = 3
        
        // Merchant pages
        const val PAGE_DASHBOARD    = 0
        const val PAGE_BAGS         = 1
        const val PAGE_LOCATIONS     = 2
        const val PAGE_MENU          = 3
    }

    //used for iOS Swipe Back Gesture
    val backGestureEnabled: MutableStateFlow<Boolean> = MutableStateFlow(false)

    init {
        setup(this)
        UtilsDefines.setModelsData()
        // Ensure language is initialized
        com.example.mystybag.sharedmodels.ModelsData.language = com.example.mystybag.core.expected.getLanguage()

        /** Enable when needed. Never set to true, just uncomment the line below */
        RemoteLogger.enabled = !isInRelease()
       // RemoteLogger.resourceID = GlobalCommonData.getResourceIDValue()
        RemoteLogger.logLevel = RemoteLogger.TYPE_ALL
        RemoteLogger.logLevel = RemoteLogger.TYPE_REST or RemoteLogger.TYPE_CUSTOM

        //CommunicationUtilsHelper.setup(CommunicationUtilsListenerImpl)
        //LogoutListenerHelper.setup(LogoutListenerImpl)

        setupPlatformHelper()

        if (Communication.token.isNotEmpty()) {
            scope.launch {
                PushTokenServerSync.awaitDevicePushTokenAndSync()
            }
        }

        val navigateToMerchantOrStaffLogin: (Boolean) -> Unit = { isStaff ->
            launchOnMain {
                navigation.replaceAll(
                    NavigatorFeatureLoginAndRegistration(
                        initialChild = if (isStaff) NavigatorStaffLogin else NavigatorMerchantLogin
                    )
                )
            }
        }
        ReLoginHelper.onNavigateToMerchantOrStaffLogin = navigateToMerchantOrStaffLogin
        ReLoginHelper.applyPendingMerchantOrStaffNavigationIfNeeded(navigateToMerchantOrStaffLogin)
        // При връщане от бекграунд след ≥10 мин (служител/търговец) – отваряме екрана за логин
        LogoutMethods.onStaffOrMerchantBackgroundTimeout = { isStaff ->
            launchOnMain {
                navigation.replaceAll(
                    NavigatorFeatureLoginAndRegistration(
                        initialChild = if (isStaff) NavigatorStaffLogin else NavigatorMerchantLogin
                    )
                )
            }
        }

       // scope.launch {
           // for (durationMillis in LogoutMethods.channel) {
                if ( isMainInStack()) {
                    launchOnMain {
                        getViewInterface().dialogControl.dismissDialog(DialogResultType.CANCELED)
                        closeBottomSheet()
                        logout()
                    }
                }
            //}
       // }

        if (getOS() == IOS) //used for iOS Swipe Back Gesture
            childStack.subscribe {
                backGestureEnabled.value = it.active.instance !is MainViewModel || it.backStack.isNotEmpty()
            }
    }

    override fun getStackKey() = "MystyBagDigitalWalletStackKey"

    override fun onCreate() {
    }

    override fun onDestroy() {
        super.onDestroy()
    }

    override fun finish(onComplete: ((Boolean) -> Unit)?) = NavigationHelper.closeScreen()

    override fun createViewModel(config: NavigatorConfig, context: AppContext): BaseViewModel {
        return when (config) {
            is FeatureManagers.Login                            -> FeatureLoginAndRegistrationViewModel(context, config.config)
            is NavigatorFeatureLoginAndRegistration             -> FeatureLoginAndRegistrationViewModel(context, config)
            // Prompt to add location (shown when merchant has no locations after login)
            is NavigatorAddLocationPrompt                        -> FeatureLoginAndRegistrationViewModel(context, config)
            is NavigatorMain                                    -> MainViewModel(context)
            is com.example.mystybag.login.logic.NavigatorReturnToDashboard -> {
                // This navigator is handled in popToMainAndNavigate, not here
                // Return empty view model as placeholder (should not be reached)
                EmptyViewModel(context)
            }
            is com.example.mystybag.login.logic.NavigatorReturnToLocations -> {
                // This navigator is handled in popToMainAndNavigate, not here
                EmptyViewModel(context)
            }
            // ========== СЪБИРАНЕ НА ДАННИ ЗА MERCHANT_LOCATION ==========
            // Step 6 (formerly Step 7): Add merchant location details
            is NavigatorMerchantRegistrationStep7 -> MerchantRegistrationAddMerchantLocationDetailsViewModel(context, config)
            // Step 8: Review merchant location
            is NavigatorMerchantRegistrationStep8 -> MerchantRegistrationReviewStoreDetailsViewModel(context, config)
            // Step 9: Merchant location add a what you need to know and a description
            is NavigatorMerchantRegistrationStep9 -> MerchantRegistrationAddDescriptionViewModel(context, config)
            // Step 9B: Merchant location add menu photos (up to 10)
            is NavigatorMerchantRegistrationStep9B -> MerchantRegistrationMenuPhotosViewModel(context, config)
            // Add location from Dashboard (simplified flow)
            is NavigatorAddMerchantLocationFromDashboard -> {
                // Start the flow with merchant data pre-filled if available
                MerchantRegistrationAddMerchantLocationDetailsViewModel(
                    context, 
                    NavigatorMerchantRegistrationStep7(
                        merchantId = config.merchantId,
                        storeName = "",
                        storeAddress = config.registeredCompanyStreet ?: "",
                        cityId = config.registeredCompanyCityId,
                        cityName = config.registeredCompanyCityName ?: "",
                        countryId = null,
                        countryName = "",
                        storePhoneNumber = config.companyPhoneNumber ?: "",
                        dialCode = config.companyDialCode ?: "",
                        returnToDashboard = true // Return to dashboard after completion
                    )
                )
            }
            is NavigatorInternetTroubleshooting                 -> InternetTroubleshootingViewModel(context)
            is NavigatorURLView                                 -> URLViewViewModel(
                context = context,
                url = config.url,
                text = config.title,
                postBody = config.postBody,
                successUrlPrefix = config.successUrlPrefix,
                cancelUrlPrefix = config.cancelUrlPrefix,
                transactionReference = config.transactionReference,
                reservationId = config.reservationId
            )
            is NavigatorPickContact                             -> PickContactViewModel(context, config.searchType)
            is NavigatorPdfUrl                                  -> PdfUrlViewModel(context, config.url, config.title, config.withSave)
            is NavigatorPdfFile                                 -> PdfFileViewModel(context, config.title, config.file, config.withSave)
            is NavigatorBagPage                                 -> BagViewModel( context, config.bagItem)
            is NavigatorProfilePage                              -> ProfilePageViewModel(context)
            is com.example.mystybag.appCore.logic.NavigatorPersonalDetails -> com.example.mystybag.appCore.logic.PersonalDetailsViewModel(context)
            is com.example.mystybag.appCore.logic.NavigatorDeleteAccountRequest -> com.example.mystybag.appCore.logic.DeleteAccountRequestViewModel(context)
            is com.example.mystybag.appCore.logic.NavigatorNotificationSettings -> com.example.mystybag.appCore.logic.pages.NotificationSettingsViewModel(context)
            is com.example.mystybag.appCore.logic.NavigatorSettings -> com.example.mystybag.appCore.logic.pages.SettingsPageViewModel(context)
            is com.example.mystybag.appCore.logic.NavigatorStoredCards -> com.example.mystybag.appCore.logic.pages.StoredCardsViewModel(context)
            is NavigatorAddBag                                   -> com.example.mystybag.appCore.logic.pages.AddBagViewModel(context, config.merchantLocationId, config.businessCategoryId, config.bagId)
            // Prompt to add first bag - show in login feature stack
            is NavigatorAddFirstBagPrompt                        -> FeatureLoginAndRegistrationViewModel(context, config)
            is NavigatorProfileCropImage                         -> ProfileImageCropperViewModel(
                context,
                config.selectedImage,
                config.callbacks,
                config.aspectRatio,
                config.outputWidth,
                config.outputHeight,
            )
            // Merchant Locations
            is NavigatorMerchantLocationsList                    -> com.example.mystybag.appCore.logic.pages.MerchantLocationsListViewModel(context)
            is NavigatorMerchantLocationDetails                  -> com.example.mystybag.appCore.logic.pages.MerchantLocationDetailsViewModel(context, config.locationId)
            is NavigatorMerchantProfileDetails -> com.example.mystybag.appCore.logic.pages.MerchantProfileDetailsViewModel(context)
            is NavigatorStaffList -> com.example.mystybag.appCore.logic.pages.StaffListViewModel(context)
            is com.example.mystybag.appCore.logic.NavigatorCompanyResponsiblePersonsList -> com.example.mystybag.appCore.logic.pages.CompanyResponsiblePersonsListViewModel(context)
            is com.example.mystybag.appCore.logic.NavigatorContacts -> com.example.mystybag.appCore.logic.pages.ContactsViewModel(context)
            is com.example.mystybag.appCore.logic.NavigatorStatements -> com.example.mystybag.appCore.logic.pages.StatementsViewModel(context)
            is com.example.mystybag.appCore.logic.NavigatorCompanyResponsiblePersonEdit -> com.example.mystybag.appCore.logic.pages.CompanyResponsiblePersonEditViewModel(context, config)
            is NavigatorAddEmployee -> com.example.mystybag.appCore.logic.pages.AddEmployeeViewModel(context)
            is com.example.mystybag.appCore.logic.NavigatorEditEmployee -> com.example.mystybag.appCore.logic.pages.EditEmployeeViewModel(context, config)
            is NavigatorCategoryBagsList                         -> com.example.mystybag.appCore.logic.pages.CategoryBagsListViewModel(context, config.businessCategoryId, config.categoryTitle, config.bags)
            is com.example.mystybag.appCore.logic.NavigatorLeaveReview -> com.example.mystybag.appCore.logic.pages.LeaveReviewViewModel(
                context = context,
                reservation = config.reservation,
                onReviewSubmitted = {
                    launchOnMain {
                        navigation.pop { }
                        childStack.last<MainViewModel>()?.pages?.value?.items?.get(PAGE_DELIVERY)?.instance?.safeCast<DeliveryPageViewModel>()?.refreshReservations()
                    }
                }
            )
            else -> EmptyViewModel(context)
        }
    }

    /** Синхронизира сървърните статистики за менюто и обновява таб „Меню“, ако е зареден. */
    private fun refreshClientMenuStatsAfterPaidReservation() {
        scope.launch {
            ReLoginHelper.refreshClientProfileAfterPaidReservation()
            launchOnMain {
                childStack.last<MainViewModel>()?.pages?.value?.items
                    ?.get(PAGE_PROFILE)?.instance?.safeCast<ProfilePageViewModel>()
                    ?.refreshProfileStats()
            }
        }
    }

    override fun getCallbacks(config: NavigatorConfig): ParentCallback {
        if (config is NavigatorBagPage) {
            return object : BagPageCallback {
                override fun onReservationSuccess(reservationId: Long) {
                    launchOnMain {
                        navigation.popTo(0)
                        val mainViewModel = childStack.last<MainViewModel>()
                        mainViewModel?.pages?.value?.items?.get(PAGE_HOME)?.instance?.safeCast<HomePageViewModel>()?.refreshBags()
                        mainViewModel?.selectPage(
                            PAGE_DELIVERY,
                            fromView = false,
                            onPageSelected = {
                                CoroutineScope(Dispatchers.Main).launch {
                                    delay(300)
                                    mainViewModel?.pages?.value?.items?.get(PAGE_DELIVERY)?.instance?.safeCast<DeliveryPageViewModel>()?.let { delivery ->
                                        delivery.onTabSelected(0)
                                        delivery.refreshReservations()
                                    }
                                }
                            }
                        )
                        CoroutineScope(Dispatchers.Main).launch {
                            delay(500)
                            mainViewModel?.pages?.value?.items?.get(PAGE_DELIVERY)?.instance?.safeCast<DeliveryPageViewModel>()?.let { delivery ->
                                delivery.onTabSelected(0)
                                delivery.refreshReservations()
                            }
                        }
                    }
                    refreshClientMenuStatsAfterPaidReservation()
                }

                override fun onEmbeddedCheckoutServerConfirmedPaid() {
                    if (!PlatformHelper.isWeb) return
                    launchOnMain {
                        val urlView = childStack.last<URLViewViewModel>()
                        if (urlView != null) {
                            urlView.onCheckoutSuccessSignal()
                        } else {
                            childStack.last<BagViewModel>()?.completePaidCheckoutNavigation()
                        }
                    }
                }

                override fun parentOpenBottomSheet(cfg: UIBottomSheetConfig) = this@MystyBagDigitalWalletViewModel.parentOpenBottomSheet(cfg)
                override fun goBack(onComplete: ((Boolean) -> Unit)?) = this@MystyBagDigitalWalletViewModel.goBack(onComplete)
            }
        }
        if (config is NavigatorURLView) {
            return object : URLViewPaymentCallback {
                override fun onPaymentSuccess(transactionReference: String, reservationId: Long) {
                    launchOnMain {
                        navigation.popTo(0)
                        val mainViewModel = childStack.last<MainViewModel>()
                        mainViewModel?.pages?.value?.items?.get(PAGE_HOME)?.instance?.safeCast<HomePageViewModel>()?.refreshBags()
                        mainViewModel?.selectPage(
                            PAGE_DELIVERY,
                            fromView = false,
                            onPageSelected = {
                                CoroutineScope(Dispatchers.Main).launch {
                                    delay(300)
                                    mainViewModel?.pages?.value?.items?.get(PAGE_DELIVERY)?.instance?.safeCast<DeliveryPageViewModel>()?.let { delivery ->
                                        delivery.onTabSelected(0)
                                        delivery.refreshReservations()
                                    }
                                }
                            }
                        )
                        CoroutineScope(Dispatchers.Main).launch {
                            delay(500)
                            mainViewModel?.pages?.value?.items?.get(PAGE_DELIVERY)?.instance?.safeCast<DeliveryPageViewModel>()?.let { delivery ->
                                delivery.onTabSelected(0)
                                delivery.refreshReservations()
                            }
                        }
                    }
                    refreshClientMenuStatsAfterPaidReservation()
                }
                override fun onPaymentFlowClosed(transactionReference: String, reservationId: Long) {
                    childStack.last<BagViewModel>()?.resetCheckoutFlowAfterReturn()
                }
                override fun parentOpenBottomSheet(cfg: UIBottomSheetConfig) = this@MystyBagDigitalWalletViewModel.parentOpenBottomSheet(cfg)
                override fun goBack(onComplete: ((Boolean) -> Unit)?) = this@MystyBagDigitalWalletViewModel.goBack(onComplete)
            }
        }
        return super.getCallbacks(config)
    }

    override fun configureBottomSheets(
        config  : UIBottomSheetConfig,
        context : AppContext
    ): BottomSheetChild {
        return when (config) {
            is BSNavigatorLanguageSelection -> {
                val viewModel = LanguageSelectionBottomSheetViewModel(context, config)
                LanguageSelectionBottomSheetChild(viewModel)
            }
            is BSNavigatorReservationDetails -> {
                val catalogBags = childStack.last<MainViewModel>()?.getHomeCatalogBagsFlattened() ?: emptyList()
                val viewModel = ReservationDetailsBottomSheetViewModel(
                    context = context,
                    reservation = config.reservation,
                    clientCatalogBags = catalogBags,
                    onClose = {
                        closeBottomSheet()
                    },
                    onReservationUpdated = {
                        // Close bottom sheet and navigate to DeliveryPage
                        closeBottomSheet {
                            launchOnMain {
                                navigation.popTo(0)
                                val mainViewModel = childStack.last<MainViewModel>()
                                // Refresh HomePage bags after successful review
                                mainViewModel?.pages?.value?.items?.get(PAGE_HOME)?.instance?.safeCast<HomePageViewModel>()?.refreshBags()
                                mainViewModel?.selectPage(
                                    PAGE_DELIVERY,
                                    fromView = false,
                                    onPageSelected = {
                                        // Refresh reservations after page is selected and ready
                                        mainViewModel?.pages?.value?.items?.get(PAGE_DELIVERY)?.instance?.safeCast<DeliveryPageViewModel>()?.refreshReservations()
                                    }
                                )
                            }
                        }
                    },
                    onOpenBag = { bagItem ->
                        closeBottomSheet {
                            launchOnMain {
                                navigate(NavigatorBagPage(bagItem))
                            }
                        }
                    }
                )
                ReservationDetailsBottomSheetChild(viewModel)
            }
            else -> super.configureBottomSheets(config, context)
        }
    }

    override fun onBottomSheetClosed(child: BottomSheetChild?) {
    }

    override fun goToMainScreen(showProgress: Boolean, onErrorCallback: (() -> Unit)?, target: NavigatorConfig?) {

        if (showProgress)
            showProgress(true)

        launchOnMain {
            when (target) {
                is NavigatorAddFirstBagPrompt -> {
                    // Merchant with 0 bags: open Main then push AddFirstBagPrompt on top
                    navigation.replaceAll(NavigatorMain)
                    navigate(target)
                }
                else -> {
                    val targetConfig = target ?: config ?: NavigatorMain
                    navigation.replaceAll(targetConfig)
                }
            }
            onErrorCallback?.invoke()
        }
    }

    override fun isMainInStack() = childStack.value.items.any { it.instance is MainViewModel }

    override fun <T: NavigatorConfig> navigatorIsOpen(clazz: KClass<T>): Boolean = try {
        childStack.value.items.any {
            it.configuration::class.simpleName?.equalsIgnoreCase(clazz.simpleName) == true
        }
    }
    catch (e: Exception) {
        logException(e)
        false
    }


    private fun onMainScreenSelectPage(index: Int = 0, scrollToTop: Boolean = false) {
        childStack.last<MainViewModel>()?.selectPage(index, scrollToTop, fromView = false)
    }


    /** Main Callback */
    override fun popToMainAndNavigate(navigatorConfig: NavigatorConfig?) {
        if (navigatorConfig is NavigatorFeatureLoginAndRegistration) {
            // For logout - replace entire stack with Welcome screen
            launchOnMain {
                navigation.replaceAll(navigatorConfig)
            }
        } else if (navigatorConfig is com.example.mystybag.login.logic.NavigatorReturnToDashboard) {
            // Replace entire stack with fresh Main so Dashboard gets full lifecycle (onCreate/onResume) and loadDashboardData().
            // popTo(0) left the old MainViewModel in place and Dashboard's onResume/loadDashboardData() was not reached.
            navigation.replaceAll(NavigatorMain)
        } else if (navigatorConfig is com.example.mystybag.login.logic.NavigatorReturnToLocations) {
            // Return to Locations tab (e.g. after skipping AddFirstBagPrompt when we came from add location)
            navigation.popTo(0)
            launchOnMain {
                childStack.last<MainViewModel>()?.selectPage(MystyBagDigitalWalletViewModel.PAGE_LOCATIONS, scrollToTop = false, fromView = false)
            }
        } else if (navigatorConfig is com.example.mystybag.login.logic.NavigatorAddFirstBagPrompt) {
            // Special handling for NavigatorAddFirstBagPrompt
            // IMPORTANT: Check stack BEFORE popTo(0), otherwise it will be empty
            val currentStack = childStack.value.items
            val hasPromptScreenInStack = currentStack.any { item ->
                item.configuration is com.example.mystybag.login.logic.NavigatorAddFirstBagPrompt
            }
            
            if (hasPromptScreenInStack) {
                // User clicked the button - we're already on AddFirstBagPrompt screen
                // Navigate directly to NavigatorAddBag WITHOUT popTo to avoid showing intermediate screen
                // Direct navigation adds AddBag screen on top, creating seamless transition
                // The prompt screen remains in stack but is hidden by the new screen
                navigate(NavigatorAddBag(
                    merchantLocationId = navigatorConfig.merchantLocationId,
                    businessCategoryId = navigatorConfig.businessCategoryId
                ))
            } else {
                // Coming from location creation: open AddFirstBagPrompt directly to avoid flicker.
                // Pop, push prompt and set Locations tab in one main-thread block so the UI updates in a single frame.
                launchOnMain {
                    navigation.popTo(0)
                    navigate(navigatorConfig)
                    childStack.last<MainViewModel>()?.selectPage(MystyBagDigitalWalletViewModel.PAGE_LOCATIONS, scrollToTop = false, fromView = false)
                }
            }
        } else if (navigatorConfig is NavigatorAddMerchantLocationFromDashboard) {
            // Coming from AddLocationPrompt "Започни" - navigate directly without popTo(0)
            // to avoid "back then forward" animation (pop to main would show back, then navigate shows forward)
            launchOnMain {
                navigate(navigatorConfig)
            }
        } else if (navigatorConfig is NavigatorSelectPage) {
            // Switch main tab (e.g. from empty Reservations to Find) without pushing a new screen
            launchOnMain {
                childStack.last<MainViewModel>()?.selectPage(navigatorConfig.pageIndex, scrollToTop = true, fromView = false)
            }
        } else if (navigatorConfig is NavigatorMerchantLocationsList) {
            // Return from edit flow: stack is [Main, Step7, Step8, Step9, Step9B]; List is Main's tab content, not in stack. Pop all steps to Main and ensure Locations tab is selected.
            launchOnMain {
                navigation.popTo(0)
                childStack.last<MainViewModel>()?.selectPage(MystyBagDigitalWalletViewModel.PAGE_LOCATIONS, scrollToTop = false, fromView = false)
            }
        } else {
            // For other navigators, first pop to main, then navigate
            // Use launchOnMain to ensure operations happen on main thread
            launchOnMain {
                navigation.popTo(0)
                navigatorConfig?.let { config ->
                    // Small delay to ensure popTo completes before navigating
                    kotlinx.coroutines.delay(150)
                    navigate(config)
                }
            }
        }
    }

    private fun goToHomeScreen() {
        launchOnMain {
            navigation.popTo(0)
            delay(500)
            onMainScreenSelectPage()
        }
    }
}